
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface Feature {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  category: string;
  created_at: string;
  updated_at: string;
}

export const useFeatures = () => {
  return useQuery({
    queryKey: ['features'],
    queryFn: async (): Promise<Feature[]> => {
      console.log('🏷️ Fetching features from Supabase...');
      
      const { data: features, error } = await supabase
        .from('features')
        .select('*')
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching features:', error);
        throw error;
      }

      console.log(`✅ Fetched ${features?.length || 0} features from Supabase`);
      return features || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

export const usePropertyFeatures = (propertyId: string) => {
  return useQuery({
    queryKey: ['property-features', propertyId],
    queryFn: async (): Promise<Feature[]> => {
      console.log(`🏷️ Fetching features for property ${propertyId}...`);
      
      const { data: propertyFeatures, error } = await supabase
        .from('property_features')
        .select(`
          features (
            id,
            name,
            description,
            icon,
            category,
            created_at,
            updated_at
          )
        `)
        .eq('property_id', propertyId);

      if (error) {
        console.error('Error fetching property features:', error);
        throw error;
      }

      // Extract features from the nested structure
      const features = propertyFeatures
        ?.map((pf: any) => pf.features)
        .filter(Boolean) as Feature[] || [];

      console.log(`✅ Fetched ${features.length} features for property ${propertyId}`);
      return features;
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// New hook to get property feature IDs for the edit form
export const usePropertyFeatureIds = (propertyId: string) => {
  return useQuery({
    queryKey: ['property-feature-ids', propertyId],
    queryFn: async (): Promise<string[]> => {
      console.log(`🏷️ Fetching feature IDs for property ${propertyId}...`);
      
      const { data: propertyFeatures, error } = await supabase
        .from('property_features')
        .select('feature_id')
        .eq('property_id', propertyId);

      if (error) {
        console.error('Error fetching property feature IDs:', error);
        throw error;
      }

      const featureIds = propertyFeatures?.map(pf => pf.feature_id) || [];
      console.log(`✅ Fetched ${featureIds.length} feature IDs for property ${propertyId}`);
      return featureIds;
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};
